import React from 'react';
import SuccessModal from '../../components/modals/SuccessModal';
import DeviceConnectionModal from '../../components/modals/DeviceConnectionModal';
import ConfirmDetailsModal from '../../components/modals/ConfirmDetailsModal';
import ExitSetupModal from '../../components/modals/ExitSetupModal';
import { useDeviceConfigFlow } from './configFlowSteps/useDeviceConfigFlow';
import Step1TurnOnDevice from './configFlowSteps/Step1TurnOnDevice';
import Step2PreflightChecks from './configFlowSteps/Step2PreflightChecks';
import Step3WifiConfig from './configFlowSteps/Step3WifiConfig';
import Step4Integrations from './configFlowSteps/Step4Integrations';
import Step5ConnectToDeviceHotspot from './configFlowSteps/Step5ConnectToDeviceHotspot';
import Step6SendWifi from './configFlowSteps/Step6SendWifi';
import Step7WaitingForInternet from './configFlowSteps/Step7WaitingForInternet';
import Step8DeviceConnected from './configFlowSteps/Step8DeviceConnected';
import Step8DeviceConnectionFailed from './configFlowSteps/Step8DeviceConnectionFailed';
import '../../styles/pages/device-config-flow-mobile.css';
import { Wifi } from 'lucide-react';

interface DeviceConfigFlowProps {
  setShowConfigFlow: React.Dispatch<React.SetStateAction<boolean>>;
  SerialNumber: string;
  deviceId?: string;
}

const DeviceConfigFlowMobile: React.FC<DeviceConfigFlowProps> = ({
  setShowConfigFlow,
  SerialNumber,
  deviceId,
}) => {
  const { ctx, popup, confirmPopup, closePopup, closeConfirmPopup } =
    useDeviceConfigFlow({
      setShowConfigFlow,
      SerialNumber,
      deviceId,
    });

  const handlePopupAction = () => {
    const cb = popup.onAction;
    closePopup();
    cb?.();
  };

  return (
    <>
      {ctx.step === 'on' && <Step1TurnOnDevice ctx={ctx} />}
      {ctx.step === 'set' && <Step2PreflightChecks ctx={ctx} />}
      {ctx.step === 'wificonf' && <Step3WifiConfig ctx={ctx} />}
      {ctx.step === 'integrations' && <Step4Integrations ctx={ctx} />}
      {ctx.step === 'deviceWifi' && <Step5ConnectToDeviceHotspot ctx={ctx} />}
      {ctx.step === 'connect' && <Step6SendWifi ctx={ctx} />}
      {ctx.step === 'wait' && <Step7WaitingForInternet ctx={ctx} />}
      {ctx.step === 'done' && <Step8DeviceConnected ctx={ctx} />}
      {ctx.step === 'failed' && <Step8DeviceConnectionFailed ctx={ctx} />}

      {popup.variant === 'device' ? (
        <DeviceConnectionModal
          isOpen={popup.isOpen}
          onClose={closePopup}
          title={popup.title}
          subtitle={popup.subtitle}
          status={popup.status}
          actionText={popup.actionText}
          onAction={popup.onAction ? handlePopupAction : undefined}
        />
      ) : (
        <SuccessModal
          isOpen={popup.isOpen}
          onClose={closePopup}
          title={popup.title}
          subtitle={popup.subtitle}
          status={popup.status}
          showAlertIcon={popup.showAlertIcon}
          actionText={popup.actionText}
          onAction={popup.onAction ? handlePopupAction : undefined}
        />
      )}

      <ExitSetupModal
        isOpen={ctx.exitModalOpen}
        onContinue={ctx.closeExitModal}
        onDiscard={ctx.exitSetup}
      />

      <ConfirmDetailsModal
        isOpen={confirmPopup.isOpen}
        onClose={closeConfirmPopup}
        onEdit={closeConfirmPopup}
        onConfirm={ctx.confirmWifiDetails}
        details={[
          { label: 'Wifi Name', value: ctx.wifiname || '—' },
          { label: 'Wifi Password', value: ctx.wifipassword || '—' },
        ]}
        description={
          <span>
            Make sure your Wi-Fi name and password are correct and you&apos;re connecting to a 2.4&nbsp;GHz network. If anything&apos;s off, the Wi-Fi LED&nbsp;<Wifi size={16} strokeWidth={2} color="var(--color-text)" className="inline align-text-top font-bold" /> will turn red within 1–2 minutes and you&apos;ll need to start over.
          {/* 
            Please double-check your Wi-Fi name and password before confirming. Make sure you&apos;re connecting to a 2.4&nbsp;GHz network, as the device does not support 5&nbsp;GHz. If the details are incorrect, the Wi-Fi LED&nbsp;<Wifi size={16} strokeWidth={2} color="var(--color-text)" className="inline align-text-top font-bold" /> will turn red within 1–2 minutes and you&apos;ll need to start the setup again. */}
          </span>
        }
      />
    </>
  );
};

export default DeviceConfigFlowMobile;
